import { Tabs, Tab, Box, Dialog, IconButton, Stack } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { MouseEvent, SyntheticEvent, useState } from 'react'
import ConfirmDeleteModalUI from '@/components/common/ConfirmDeleteModalUI/ConfirmDeleteModalUI'
import { useSavedViewsContext } from '@/features/config/contexts/saved-views-context'
import useTranslation from '@/hooks/useTranslation'
import type { GridColumnState } from '@/components/grid/types'

interface SavedViewsTabsProps {
  onApplyColumns: (columns: GridColumnState[]) => void
  /** Match the host toolbar's button height. */
  height?: number
  /** Match the host toolbar's button radius. */
  radius?: number
}

/**
 * The saved views, as a segmented control meant to sit beside the Columns
 * button that manages them.
 *
 * Sized to its content rather than stretched: the strip is a short list of
 * names, and giving it the leftover width left it floating in the middle of
 * the toolbar, a long way from the control it belongs to. It scrolls only
 * once the names outgrow the cap.
 */
const SavedViewsTabs = ({
  onApplyColumns,
  height = 38,
  radius = 8,
}: SavedViewsTabsProps) => {
  const { t } = useTranslation()
  const chipHeight = height - 8
  const { savedViews, activeViewName, applyView, deleteView, canPersist } =
    useSavedViewsContext()
  const [viewToDelete, setViewToDelete] = useState<string | null>(null)

  const hasActive =
    activeViewName !== null && savedViews.some((v) => v.name === activeViewName)

  const handleChange = (_: SyntheticEvent, name: string) => {
    applyView(name, onApplyColumns)
  }

  // The X sits inside the tab, so its click has to be kept from reaching the
  // tab underneath — deleting a view should never also apply it.
  const handleRequestDelete = (event: MouseEvent, name: string) => {
    event.stopPropagation()
    setViewToDelete(name)
  }

  const handleConfirmDelete = () => {
    if (!viewToDelete) return
    deleteView(viewToDelete)
    setViewToDelete(null)
  }

  if (savedViews.length === 0) return null

  return (
    <Box
      sx={{
        minWidth: 0,
        maxWidth: 360,
        flexShrink: 1,
        height: `${height}px`,
        p: '3px',
        borderRadius: `${radius}px`,
        border: '1px solid',
        borderColor: 'border.main',
        // An inset track, so the group reads as one control sitting next to
        // the toolbar buttons rather than as more buttons.
        backgroundColor: 'background.overrideDefault',
      }}
    >
      <Tabs
        value={hasActive ? activeViewName : false}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: chipHeight,
          height: chipHeight,
          // The selected view is drawn as a raised chip below, which is the
          // whole point of a segmented control; an underline as well would be
          // saying the same thing twice.
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTabs-flexContainer': { gap: '2px', height: chipHeight },
          '& .MuiTabs-scrollButtons': {
            width: 20,
            color: 'text.main',
            '&.Mui-disabled': { opacity: 0.25 },
          },
          '& .MuiTab-root': {
            minHeight: chipHeight,
            height: chipHeight,
            minWidth: 0,
            px: 1.5,
            py: 0,
            borderRadius: `${Math.max(radius - 2, 2)}px`,
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 500,
            maxWidth: 150,
            // `text.main` at reduced weight rather than a grey token: the greys
            // are a single value for both themes and go muddy on the dark
            // track.
            color: 'text.main',
            opacity: 0.6,
            transition: 'background-color .15s, opacity .15s',
            '&:hover': { opacity: 1 },
            '&.Mui-selected': {
              opacity: 1,
              color: 'primary.main',
              fontWeight: 600,
              backgroundColor: 'background.default',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.12)',
            },
            '&.Mui-disabled': { opacity: 0.35 },
          },
        }}
      >
        {savedViews.map((view) => (
          <Tab
            key={view.name}
            value={view.name}
            label={
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {view.name}
                </Box>
                <IconButton
                  component="span"
                  size="small"
                  aria-label={`Delete view ${view.name}`}
                  onClick={(event) => handleRequestDelete(event, view.name)}
                  disabled={!canPersist}
                  sx={{
                    p: '1px',
                    ml: '2px',
                    color: 'inherit',
                    opacity: 0.55,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Stack>
            }
            // Activating a view writes it back as the active one. Until the
            // server copy has loaded that write would be lost on the next
            // load, so the tab reads rather than pretends.
            disabled={!canPersist}
          />
        ))}
      </Tabs>

      <Dialog
        open={Boolean(viewToDelete)}
        onClose={() => setViewToDelete(null)}
      >
        <ConfirmDeleteModalUI
          title={t('savedViewsDeleteTitle')}
          text={t('savedViewsDeleteConfirm', { name: viewToDelete ?? '' })}
          onClose={() => setViewToDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      </Dialog>
    </Box>
  )
}

export default SavedViewsTabs
